// The editorial guide, when present, is the source of weight limits for this product.
export const sizeKey = (name) => {
  const key = String(name).toLowerCase().replace(/[–—]/g, '-').replace(/\s+/g, '');
  return key === 'pm' ? key : key.replace(/m(?:eses)?$/, '');
};
const number = (value) => Number(String(value).replace(',', '.'));
export const clothingWeightLimits = Object.freeze([
  ['pm', 2], ['rn', 3.5], ['0-3', 5.3], ['3-6', 6.8],
  ['6-9', 8.6], ['9-12', 10.4], ['12-18', 11.8], ['18-24', 13],
].map(Object.freeze));

export function parseRanges(raw) {
  return String(raw || '').split('\n').filter((line) => line.trim()).map((line) => {
    const [name = '', weightText = '', ageText = ''] = line.split('|').map((part) => part.trim());
    const bounds = (text) => (text.match(/\d+(?:[.,]\d+)?/g) || []).map(number);
    const weight = bounds(weightText);
    const age = bounds(ageText);
    return { name, weightText, ageText, weightMin: weight[0], weightMax: weight[1] ?? weight[0], ageMin: age[0], ageMax: age[1] ?? age[0] };
  });
}

export function resolveRanges(raw, sizes, editorial = [], useClothingMaxima = false) {
  const fallback = parseRanges(raw);
  if (useClothingMaxima && !editorial.length) {
    return sizes.flatMap((name) => {
      const key = sizeKey(name);
      const limit = clothingWeightLimits.find(([label]) => label === key);
      if (!limit) return [];
      const original = fallback.find((range) => sizeKey(range.name) === key);
      const age = /^(\d+)-(\d+)$/.exec(key);
      return [{
        name, maximumOnly: true, weightMax: limit[1],
        weightText: `${limit[1]} kg`,
        ageMin: age ? Number(age[1]) : original?.ageMin,
        ageMax: age ? Number(age[2]) : original?.ageMax,
        ageText: age ? `${age[1]}–${age[2]} m` : original?.ageText || '',
      }];
    });
  }
  const source = editorial.length ? editorial.map((card) => {
    const original = fallback.find((range) => sizeKey(range.name) === sizeKey(card.name));
    return {
      ...original,
      name: card.name,
      weightMin: number(card.weightMin),
      weightMax: number(card.weightMax),
      weightText: `${card.weightMin}–${card.weightMax} kg`,
      ageText: card.ageText || original?.ageText || '',
    };
  }) : fallback;
  return source.flatMap((range) => {
    const name = sizes.find((size) => sizeKey(size) === sizeKey(range.name));
    return name ? [{ ...range, name }] : [];
  });
}

export function recommendSizes(ranges, weightValue, ageValue) {
  const hasWeight = String(weightValue).trim() !== '';
  const hasAge = String(ageValue).trim() !== '';
  if (!hasWeight && !hasAge) return null;
  // Weight is the more useful fit criterion. Never extrapolate beyond the guide.
  const value = number(hasWeight ? weightValue : ageValue);
  if (!Number.isFinite(value) || value < 0 || (hasWeight && value === 0)) return [];
  if (ranges.some((range) => range.maximumOnly)) {
    if (hasWeight) {
      const reference = clothingWeightLimits.find(([, maximum]) => value <= maximum);
      return reference ? ranges.filter((range) => sizeKey(range.name) === reference[0]) : [];
    }
    return ranges.filter((range) => Number.isFinite(range.ageMin) && Number.isFinite(range.ageMax)
      && value >= range.ageMin && value < range.ageMax);
  }
  const min = hasWeight ? 'weightMin' : 'ageMin';
  const max = hasWeight ? 'weightMax' : 'ageMax';
  return ranges.filter((range) => Number.isFinite(range[min]) && Number.isFinite(range[max]) && value >= range[min] && value <= range[max]);
}

function readJSON(element, fallback) {
  try { return JSON.parse(element?.textContent || ''); } catch { return fallback; }
}

function bindDialog(dialog) {
  if (dialog.dataset.sizeGuideReady) return;
  dialog.dataset.sizeGuideReady = 'true';
  const root = dialog.closest('[data-mm-pdp]');
  if (!root) return;
  const weightInput = dialog.querySelector('[data-mm-size-input="weight"]');
  const ageInput = dialog.querySelector('[data-mm-size-input="age"]');
  const result = dialog.querySelector('[data-mm-size-result]');
  const resultLabel = dialog.querySelector('[data-mm-size-result-label]');
  const resultSize = dialog.querySelector('[data-mm-size-result-size]');
  const resultNote = dialog.querySelector('[data-mm-size-result-note]');
  const messages = readJSON(dialog.querySelector('[data-mm-size-messages]'), {});
  const description = dialog.querySelector('.mm-pdp__sizeguide-sub');
  const originalDescription = description?.textContent;
  const weightFormatter = new Intl.NumberFormat(document.documentElement.lang || 'es');
  const weightText = (range) => range.maximumOnly
    ? messages.maximumWeight.replace('__WEIGHT__', weightFormatter.format(range.weightMax))
    : range.weightText;
  let ranges = [];

  const recommend = () => {
    const matches = recommendSizes(ranges, weightInput.value, ageInput.value);
    result.hidden = matches === null;
    if (matches === null) return;
    resultLabel.textContent = matches.length
      ? (matches[0].maximumOnly ? messages.reference : messages.recommendation)
      : messages.noMatchTitle;
    resultSize.textContent = matches.map((range) => range.name).join(' / ');
    resultSize.hidden = !matches.length;
    resultNote.textContent = matches.length
      ? matches.map((range) => `${range.name}: ${weightText(range)}`).join(' · ')
        + (matches[0].maximumOnly ? `. ${messages.fitNote}` : '')
      : messages.noMatch;
  };

  root.querySelectorAll('[data-mm-size-guide-open]').forEach((button) => {
    button.addEventListener('click', (event) => {
      event.preventDefault();
      // Resolve on open so the later editorial section and theme editor changes are available.
      const guide = Array.from(document.querySelectorAll('[data-mm-editorial-size-guide]'))
        .find((element) => element.dataset.productId === dialog.dataset.productId);
      ranges = resolveRanges(
        readJSON(dialog.querySelector('[data-mm-size-ranges]'), ''),
        readJSON(dialog.querySelector('[data-mm-size-options]'), []),
        readJSON(guide, []),
        messages.clothingMaxima === true,
      );
      if (description) description.textContent = messages.clothingMaxima && !guide
        ? messages.maximumDescription : originalDescription;
      const rows = ranges.map((range) => {
        const row = document.createElement('tr');
        [range.name, weightText(range), range.ageText].forEach((text, index) => {
          const cell = document.createElement(index ? 'td' : 'th');
          if (!index) cell.scope = 'row';
          cell.textContent = text;
          row.append(cell);
        });
        return row;
      });
      dialog.querySelector('[data-mm-size-chart-tbody]').replaceChildren(...rows);
      recommend();
      dialog.showModal();
    });
  });
  weightInput.addEventListener('input', recommend);
  ageInput.addEventListener('input', recommend);
  const toggle = dialog.querySelector('[data-mm-size-chart-toggle]');
  const chart = dialog.querySelector('[data-mm-size-chart-body]');
  toggle.addEventListener('click', () => {
    chart.hidden = !chart.hidden;
    toggle.setAttribute('aria-expanded', String(!chart.hidden));
    toggle.classList.toggle('is-open', !chart.hidden);
  });
  dialog.querySelector('[data-mm-size-guide-close]').addEventListener('click', () => dialog.close());
  dialog.addEventListener('click', (event) => { if (event.target === dialog) dialog.close(); });
}

if (typeof document !== 'undefined') {
  const init = () => document.querySelectorAll('[data-mm-size-guide]').forEach(bindDialog);
  init();
  document.addEventListener('shopify:section:load', init);
}
